import apple, { AppleMusic } from "../services/apple";
import { AsyncEvent } from "../util";
import { ServicePlayer, type PlayerDevice } from "./service_player";
import type { PlaybackState, RepeatMode } from "./types";

type MusicKitInstance = any;
type MusicKitPlaybackStateEvent = any;

enum State {
  none = 0,
  loading = 1,
  playing = 2,
  paused = 3,
  stopped = 4,
  ended = 5,
  seeking = 6,
  waiting = 8,
  stalled = 9,
  completed = 10,
}

export class ApplePlayer extends ServicePlayer {
  service = "apple" as const;
  supportsDevices = false;

  private client: AppleMusic;
  private playTrackNonce = 0;

  constructor(client?: AppleMusic) {
    super();
    this.client = client || apple;
  }

  private get musicKit(): MusicKitInstance {
    return this.client.musicKit;
  }

  async init(): Promise<void> {
    this.musicKit.addEventListener(
      "playbackStateDidChange",
      (event: MusicKitPlaybackStateEvent) => {
        console.debug("Musickit event playbackStateDidChange", event);
        const newState: State = event.state;

        const alwaysPaused = [
          State.none,
          State.stopped,
          State.ended,
          State.completed,
          State.paused,
        ];

        if (newState === State.playing) {
          this.playbackState.isPaused = false;
        } else if (alwaysPaused.includes(newState)) {
          this.playbackState.isPaused = true;
        }
        // For other states we assume it's intermediate (e.g. next track is buffering)
        // and keep the previous state
        if ([State.loading, State.waiting, State.seeking].includes(newState)) {
          this.playbackState.isLoading = true;
        } else {
          this.playbackState.isLoading = false;
        }

        // Set current track
        const mediaItem = this.musicKit.nowPlayingItem;
        if (mediaItem) {
          const attributes = mediaItem.attributes;
          this.playbackState.currentTrack = {
            id: mediaItem.id,
            name: attributes.name,
            artists: [{ name: attributes.artistName }],
            album: {
              name: attributes.albumName,
            },
            durationMs: attributes.durationInMillis,
            image: attributes.artwork.url,
          };
        } else {
          this.playbackState.currentTrack = null;
        }

        this.dispatchState();
      }
    );
    const musicKit = this.musicKit;

    musicKit.addEventListener("nowPlayingItemDidChange", (event: any) => {
      console.debug("Musickit event nowPlayingItemDidChange", event);
      const mediaItem = this.musicKit.nowPlayingItem;
      if (mediaItem) {
        const attributes = mediaItem.attributes;
        this.playbackState.currentTrack = {
          id: mediaItem.id,
          name: attributes.name,
          artists: [{ name: attributes.artistName }],
          album: {
            name: attributes.albumName,
          },
          durationMs: attributes.durationInMillis,
          image: attributes.artwork.url,
        };
      } else {
        this.playbackState.currentTrack = null;
      }
      this.dispatchState();
    });

    musicKit.addEventListener("playbackTimeDidChange", (event: any) => {
      const oldState = this.playbackState.progressMs;
      const newState = event.currentPlaybackTime * 1000;
      if (newState === oldState) {
        // apple event is only to 1s precision, but it's fired more often. Let's not spam
        return;
      }
      this.playbackState.progressMs = newState;
      this.dispatchState();
    });

    musicKit.addEventListener("playbackVolumeDidChange", (_: any) => {
      console.debug("Musickit event playbackVolumeDidChange", _);
      this.playbackState.volume = this.musicKit.player.volume;
      this.dispatchState();
    });

    console.debug("Apple player init done");
  }

  async pause(): Promise<void> {
    this.musicKit.pause();
  }

  async resume(): Promise<void> {
    await this.musicKit.play();
  }

  async seek(positionMs: number): Promise<void> {
    await this.musicKit.seekToTime(Math.floor(positionMs / 1000));
  }

  async nextTrack(): Promise<void> {
    await this.musicKit.skipToNextItem();
  }

  async previousTrack(): Promise<void> {
    await this.musicKit.skipToPreviousItem();
  }

  private async stop(): Promise<void> {
    // stop player and wait until stopped
    // This is needed before any start playback methods because
    // everything happens async and player must be stopped before change queue and play can be called
    if (!this.musicKit.isPlaying) {
      return;
    }
    const waiter = new AsyncEvent();
    const listener = () => {
      if (this.musicKit.isPlaying) {
        return;
      }
      document.removeEventListener(
        "umg:player:playbackStateChanged",
        listener,
        false
      );
      waiter.trigger();
    };

    document.addEventListener("umg:player:playbackStateChanged", listener);
    this.musicKit.stop();
    await waiter.wait();
  }

  private async play(): Promise<void> {
    try {
      await this.musicKit.play();
    } catch (e) {
      if (
        (e as Error).message &&
        (e as Error).message.includes("media provider object was not suitable")
      ) {
        console.error("Playback unsupported", e);
        document.dispatchEvent(
          new CustomEvent("umg:player:initializationError", {
            detail: e,
          })
        );
      } else {
        throw e;
      }
    }
  }

  private async changeToMediaAtIndex(index: number): Promise<void> {
    try {
      await this.musicKit.changeToMediaAtIndex(index);
    } catch (e) {
      if (
        (e as Error).message &&
        (e as Error).message.includes("media provider object was not suitable")
      ) {
        console.error("Playback unsupported", e);
        document.dispatchEvent(
          new CustomEvent("umg:player:initializationError", {
            detail: e,
          })
        );
      } else {
        throw e;
      }
    }
  }

  async playPlaylist(
    playlistId: string,
    options?: { position?: number; deviceId?: string }
  ): Promise<void> {
    await this.stop();
    await this.musicKit.setQueue({
      playlist: playlistId,
      startPosition: options?.position,
    });
    await this.play();
  }

  async playAlbum(
    albumId: string,
    options?: { position?: number; deviceId?: string }
  ): Promise<void> {
    await this.stop();
    await this.musicKit.setQueue({
      album: albumId,
      startPosition: options?.position,
    });

    await this.play();
  }

  async playTrack(
    trackId: string,
    options?: { positionMs?: number; deviceId?: string }
  ): Promise<void> {
    await this.stop();
    // If the song is already in queue we can just switch to it.
    // Bonus is in this case it might already be buffered
    let queueIndex = this.musicKit.queue.items.findIndex(
      (i: { id: string }) => i.id === trackId
    );
    if (queueIndex < 0) {
      // Not in queue so we replace the queue
      await this.musicKit.setQueue({ song: trackId });
      queueIndex = 0;
    }

    const requestedMs = options?.positionMs || 0;
    const requestedSecond = Math.floor((options?.positionMs || 0) / 1000);

    if (requestedSecond === 0) {
      await this.changeToMediaAtIndex(queueIndex);
      return;
    }
    this.playTrackNonce += 1;
    const nonce = this.playTrackNonce;
    const onPlaybackState = async (
      event: CustomEvent<PlaybackState>
    ): Promise<void> => {
      if (event.detail.isLoading || event.detail.isPaused) {
        return;
      }
      if (Math.abs(requestedMs - event.detail.progressMs) > 2000) {
        document.removeEventListener(
          "umg:player:playbackStateChanged",
          onPlaybackState
        );
        // Check that another play call isn't made in the meantime, in which case ignore this
        if (
          event.detail.currentTrack?.id === trackId &&
          nonce === this.playTrackNonce
        ) {
          await this.musicKit.seekToTime(Math.floor(requestedMs / 1000));
        }
      }
    };

    document.addEventListener(
      "umg:player:playbackStateChanged",
      onPlaybackState
    );

    await this.changeToMediaAtIndex(queueIndex);
  }

  async playTracks(trackIds: string[]): Promise<void> {
    let effectiveTracks = trackIds;
    if (trackIds.length > 300) {
      console.warn(
        `Apple Musickit only supports max 300 tracks in a queue, the given tracklist has ${trackIds.length} tracks. It will be truncated.`
      );
      effectiveTracks = trackIds.slice(0, 300);
    }

    await this.stop();
    await this.musicKit.setQueue({ songs: effectiveTracks });
    await this.musicKit.changeToMediaAtIndex(0);
    await this.musicKit.play();
  }

  async enqueueTracks(trackIds: string[]): Promise<void> {
    await this.musicKit.setQueue({ songs: trackIds });
  }

  async getDevices(): Promise<PlayerDevice[]> {
    return [];
  }

  async setDevice(_: string): Promise<void> {
    // noop
  }

  async togglePlayback(shuffle: boolean): Promise<void> {
    // @ts-ignore  setting shuffle mode is undocumented in musickit
    this.musicKit.shuffleMode = shuffle ? 1 : 0;
  }

  async setRepeatMode(_: RepeatMode): Promise<void> {
    throw new Error("Repeat for Apple Music not implemented");
  }

  async setVolume(volume: number): Promise<void> {
    this.musicKit.volume = volume;
  }
}
